import React, { useState } from 'react';
import { 
  LightBulbIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
  InformationCircleIcon,
  CursorArrowRippleIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ArrowsPointingOutIcon,
  ArrowPathIcon,
  CommandLineIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface Tip {
  id: number;
  category: 'general' | 'shortcuts' | 'workflow' | 'activities';
  icon: React.ReactNode;
  title: string;
  content: string;
  shortcut?: string;
}

const tips: Tip[] = [
  {
    id: 1,
    category: 'general',
    icon: <CursorArrowRippleIcon className="h-5 w-5" />,
    title: 'Drag and Drop',
    content: 'Drag activities from the sidebar and drop them onto the canvas to add them to your workflow.',
  },
  {
    id: 2,
    category: 'shortcuts',
    icon: <DocumentDuplicateIcon className="h-5 w-5" />,
    title: 'Duplicate Node',
    content: 'Select a node and press Ctrl+D to duplicate it with all its configurations.',
    shortcut: 'Ctrl+D'
  },
  {
    id: 3,
    category: 'shortcuts',
    icon: <TrashIcon className="h-5 w-5" />,
    title: 'Delete Elements',
    content: 'Select nodes or edges and press Delete or Backspace to remove them.',
    shortcut: 'Delete'
  },
  {
    id: 4,
    category: 'general',
    icon: <InformationCircleIcon className="h-5 w-5" />,
    title: 'Edit Properties',
    content: 'Double-click any node to open its properties and configure its behavior.',
  },
  {
    id: 5,
    category: 'workflow',
    icon: <ArrowPathIcon className="h-5 w-5" />,
    title: 'Connect Nodes',
    content: 'Click and drag from a node handle to another node to create connections. Hover over handles to see them highlighted.',
  },
  {
    id: 6,
    category: 'shortcuts',
    icon: <ArrowsPointingOutIcon className="h-5 w-5" />,
    title: 'Fit to View',
    content: 'Use the controls in the bottom-left corner to fit all nodes in view or zoom in/out.',
    shortcut: 'Ctrl+0'
  },
  {
    id: 7,
    category: 'workflow',
    icon: <CheckCircleIcon className="h-5 w-5" />,
    title: 'Validation',
    content: 'Click the Validate button to check your workflow for errors before saving or publishing.',
  },
  {
    id: 8,
    category: 'activities',
    icon: <CommandLineIcon className="h-5 w-5" />,
    title: 'Script Activity',
    content: 'Use Script activities to run custom JavaScript or Python code. Access workflow context, input, and output objects.',
  },
  {
    id: 9,
    category: 'activities',
    icon: <LightBulbIcon className="h-5 w-5" />,
    title: 'Approval Activity',
    content: 'Configure approvals with specific users, groups, roles, or dynamic expressions. Set timeout and escalation rules.',
  },
  {
    id: 10,
    category: 'general',
    icon: <InformationCircleIcon className="h-5 w-5" />,
    title: 'Mini Map',
    content: 'The mini-map in the bottom-right shows an overview of your entire workflow. Click to navigate quickly.',
  },
];

const TipsPanel: React.FC = () => {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'general' | 'shortcuts' | 'workflow' | 'activities'>('all');

  const filteredTips = selectedCategory === 'all' 
    ? tips 
    : tips.filter(tip => tip.category === selectedCategory);

  const currentTip = filteredTips[currentTipIndex % filteredTips.length];

  const nextTip = () => {
    setCurrentTipIndex((prev) => (prev + 1) % filteredTips.length);
  };

  const prevTip = () => {
    setCurrentTipIndex((prev) => (prev - 1 + filteredTips.length) % filteredTips.length);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'shortcuts':
        return 'text-purple-600 bg-purple-50';
      case 'workflow':
        return 'text-blue-600 bg-blue-50';
      case 'activities':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (!isExpanded) {
    return (
      <div className="absolute right-4 top-4 z-40">
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-white rounded-full shadow-lg p-3 hover:shadow-xl transition-shadow border border-gray-200 group"
          title="Show Tips"
        >
          <LightBulbIcon className="h-6 w-6 text-yellow-500 group-hover:text-yellow-600" />
          <span className="absolute -top-2 -left-2 w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></span>
        </button>
      </div>
    );
  }

  return (
    <div className="absolute right-4 top-4 z-40 w-80">
      <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <LightBulbIcon className="h-5 w-5 text-white" />
              <h3 className="text-white font-medium">Workflow Tips</h3>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-white hover:text-gray-200 transition-colors"
              title="Minimize"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                selectedCategory === 'all' 
                  ? 'bg-primary-100 text-primary-700' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              All Tips
            </button>
            <button
              onClick={() => setSelectedCategory('general')}
              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                selectedCategory === 'general' 
                  ? 'bg-gray-200 text-gray-700' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              General
            </button>
            <button
              onClick={() => setSelectedCategory('shortcuts')}
              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                selectedCategory === 'shortcuts' 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              Shortcuts
            </button>
            <button
              onClick={() => setSelectedCategory('workflow')}
              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                selectedCategory === 'workflow' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              Workflow
            </button>
            <button
              onClick={() => setSelectedCategory('activities')}
              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                selectedCategory === 'activities' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              Activities
            </button>
          </div>
        </div>

        {/* Tip Content */}
        <div className="p-4">
          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-lg ${getCategoryColor(currentTip.category)}`}>
              {currentTip.icon}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 text-sm mb-1">
                {currentTip.title}
              </h4>
              {currentTip.shortcut && (
                <div className="mb-2">
                  <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">
                    {currentTip.shortcut}
                  </kbd>
                </div>
              )}
              <p className="text-xs text-gray-600 leading-relaxed">
                {currentTip.content}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between">
            <button
              onClick={prevTip}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              title="Previous Tip"
            >
              <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
            </button>
            
            <div className="flex space-x-1">
              {filteredTips.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTipIndex(index)}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    index === currentTipIndex % filteredTips.length
                      ? 'bg-primary-500'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  title={`Tip ${index + 1}`}
                />
              ))}
            </div>
            
            <button
              onClick={nextTip}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              title="Next Tip"
            >
              <ChevronRightIcon className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Tip Counter */}
        <div className="px-4 pb-3 pt-0">
          <div className="text-center text-xs text-gray-500">
            Tip {(currentTipIndex % filteredTips.length) + 1} of {filteredTips.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TipsPanel;